import React, { useState, useEffect, useCallback } from "react";
import { AlertTriangle, ShieldCheck } from "lucide-react";

// Use sessionStorage per security review recommendation. Trade-off: users will be
// asked to re-confirm age each new browser session. Age verification flags are
// non-sensitive but per-session storage is preferred over localStorage.
const STORAGE_KEY = "pfm_age_verified";

function getStoredVerification() {
  try {
    return sessionStorage.getItem(STORAGE_KEY) === "true";
  } catch {
    return false;
  }
}

function storeVerification() {
  try {
    sessionStorage.setItem(STORAGE_KEY, "true");
  } catch {
    // sessionStorage unavailable (private mode etc.) — silently no-op
  }
}

const AgeGate = () => {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    if (!getStoredVerification()) {
      setIsVisible(true);
      document.body.style.overflow = "hidden";
    }
  }, []);

  const handleConfirm = useCallback(() => {
    storeVerification();
    setIsVisible(false);
    document.body.style.overflow = "";
  }, []);

  const handleDeny = useCallback(() => {
    window.location.href = "https://www.google.com";
  }, []);

  if (!isVisible) return null;

  return (
    <div
      className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-[10000] p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="age-gate-title"
      data-testid="age-gate-modal"
    >
      <div className="w-full max-w-md bg-white border border-[#e8c5b8] rounded-2xl p-6 sm:p-8 shadow-2xl">
        <div className="flex justify-center mb-6">
          <div className="w-16 h-16 bg-[#fdf2f4] rounded-full flex items-center justify-center">
            <ShieldCheck className="w-8 h-8 text-[#b89a6d]" />
          </div>
        </div>

        <h2
          id="age-gate-title"
          className="text-2xl font-bold text-[#3a2a26] text-center mb-3"
        >
          Age Verification Required
        </h2>

        <p className="text-[#7a6b62] text-center mb-6 leading-relaxed">
          This website contains research peptide product information intended for adults only.
          Please confirm you are <span className="text-[#b89a6d] font-semibold">18 years or older</span> to continue.
        </p>

        <div className="bg-[#fdf2f4] border border-[#f0d8d4] rounded-lg p-4 mb-6">
          <div className="flex items-start gap-3">
            <AlertTriangle className="w-5 h-5 text-[#b89a6d] flex-shrink-0 mt-0.5" />
            <p className="text-sm text-[#5a4a40]">
              Products are <strong className="text-[#3a2a26]">research chemicals for laboratory use only</strong>.
              Not medical advice. Consult a licensed healthcare professional.
            </p>
          </div>
        </div>

        <div className="flex flex-col sm:flex-row gap-3">
          <button
            onClick={handleDeny}
            className="flex-1 px-6 py-3 bg-[#f7e8e4] border border-[#e8c5b8] text-[#5a4a40] rounded-xl font-medium hover:bg-[#f0d8d4] hover:border-[#d4a5ad] transition-all"
            data-testid="age-gate-deny-btn"
          >
            I am under 18
          </button>
          <button
            onClick={handleConfirm}
            className="flex-1 px-6 py-3 bg-gradient-to-r from-[#d4a5ad] to-[#a08560] text-[#3a2a26] rounded-xl font-bold hover:from-[#c4949c] hover:to-[#a08560] transition-all shadow-lg shadow-[#d4a5ad]/30"
            data-testid="age-gate-confirm-btn"
          >
            I am 18+
          </button>
        </div>

        <p className="text-xs text-[#a09186] text-center mt-4">
          By entering, you agree to our Terms of Service and acknowledge our Disclaimer.
        </p>
      </div>
    </div>
  );
};

export default AgeGate;
